import { useState, useEffect } from "react";
import { MessageSquare } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";

interface ItemNotesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  productName: string;
  currentNotes: string;
  onSaveNotes: (notes: string) => void;
}

const COMMON_SUGGESTIONS = [
  "Sem gelo",
  "Com limão",
  "Bem gelado",
  "Sem cebola",
  "Sem alho",
  "Bem passado",
  "Ao ponto",
  "Mal passado",
  "Sem sal",
  "Extra queijo",
  "Sem molho",
  "À parte",
];

export function ItemNotesDialog({
  open,
  onOpenChange,
  productName,
  currentNotes,
  onSaveNotes,
}: ItemNotesDialogProps) {
  const [notes, setNotes] = useState(currentNotes);

  useEffect(() => {
    if (open) {
      setNotes(currentNotes);
    }
  }, [open, currentNotes]);

  const handleSuggestionClick = (suggestion: string) => {
    if (notes) {
      // Add comma separator if there's already text
      setNotes((prev) => `${prev}, ${suggestion}`);
    } else {
      setNotes(suggestion);
    }
  };

  const handleSave = () => {
    onSaveNotes(notes);
    onOpenChange(false);
  };

  const handleClear = () => {
    setNotes("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-primary" />
            Observação
          </DialogTitle>
          <p className="text-sm text-muted-foreground">{productName}</p>
        </DialogHeader>

        <div className="space-y-4">
          {/* Quick suggestions */}
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground font-medium">
              Sugestões rápidas:
            </p>
            <div className="flex flex-wrap gap-2">
              {COMMON_SUGGESTIONS.map((suggestion) => (
                <Badge
                  key={suggestion}
                  variant="outline"
                  className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors py-1.5 px-3 text-sm"
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  {suggestion}
                </Badge>
              ))}
            </div>
          </div>

          {/* Text area */}
          <Textarea
            placeholder="Digite a observação do item..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="min-h-[100px] text-base resize-none"
            autoFocus
          />
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="ghost"
            onClick={handleClear}
            className="w-full sm:w-auto"
          >
            Limpar
          </Button>
          <Button onClick={handleSave} className="w-full sm:w-auto">
            Salvar Observação
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
